import { useCallback, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import Loading from '@components/Loading/Loading';
import styles from '@components/Video/VideoPlayer.styles';
type PropsType = {
  video: string;
  loop: boolean;
  poster: string;
  play?: boolean;
  onLoaded?: () => void;
  onEnded?: () => void;
  onError?: () => void;
  withSound?: boolean;
  controls?: boolean;
  fitVideo?: boolean;
  fullscreen?: boolean;
};
const IOSVideoPlayer = ({
  video,
  loop,
  poster,
  play = false,
  onLoaded,
  onEnded,
  onError,
  withSound = false,
  controls = false,
  fitVideo = false,
  fullscreen = false
}: PropsType) => {
  const [isPlaying, setIsPlaying] = useState(play);
  const [isLoading, setIsLoading] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const playVideo = useCallback(async () => {
    if (videoRef.current) {
      try {
        videoRef.current.muted = !withSound;
        await videoRef.current.play();
        setIsPlaying(true);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error autoplaying video:', error);
        }
      }
    }
  }, [withSound]);
  const stopVideo = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };
  const handlePlay = () => {
    if (videoRef.current) {
      void videoRef.current.play();
      setIsPlaying(true);
    }
  };
  const handleLoadedMetadata = () => {
    setIsLoading(false);
    if (onLoaded) onLoaded();
    if (play) {
      playVideo();
    }
  };
  const handleEnded = () => {
    setIsPlaying(false);
    if (onEnded) onEnded();
  };
  const handleError = () => {
    setIsLoading(false);
    if (onError) onError();
  };
  useEffect(() => {
    const handleUserInteraction = () => {
      if (play) {
        playVideo();
      }
    };

    // Nasłuchiwanie na interakcję użytkownika dla autoplay na iOS
    document.addEventListener('touchstart', handleUserInteraction, {
      once: true
    });
    return () => {
      document.removeEventListener('touchstart', handleUserInteraction);
    };
  }, [play, playVideo]);
  useEffect(() => {
    if (play) {
      playVideo();
      // Próby odtwarzania w różnych momentach dla lepszej kompatybilności
      const timeouts = [setTimeout(playVideo, 100), setTimeout(playVideo, 500), setTimeout(playVideo, 1000)];
      return () => timeouts.forEach(clearTimeout);
    } else {
      stopVideo();
    }
    return undefined;
  }, [play, playVideo]);
  return <div style={{
    position: 'relative',
    width: '100%',
    height: '100%'
  }}>
      {isLoading && <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      zIndex: 1
    }}>
          {poster ? <Image src={poster} alt="" fill sx={styles.thumbnail} style={{
        objectFit: 'cover'
      }} /> : <Loading />}
        </div>}

      {!isPlaying && !isLoading && <button type="button" onClick={handlePlay} style={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      background: 'rgba(0, 0, 0, 0.5)',
      border: 'none',
      borderRadius: '50%',
      width: '80px',
      height: '80px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      zIndex: 2
    }}>
          <div style={{
        width: 0,
        height: 0,
        borderLeft: '20px solid white',
        borderTop: '12px solid transparent',
        borderBottom: '12px solid transparent',
        marginLeft: '4px'
      }} />
        </button>}

      <video ref={videoRef} src={video} playsInline webkit-playsinline="true" x5-playsinline="true" x-webkit-airplay="allow" loop={loop} muted={!withSound} controls={controls} disablePictureInPicture={!fullscreen} preload="auto" style={{
      width: '100%',
      height: '100%',
      objectFit: fitVideo ? 'contain' : 'cover',
      display: isLoading ? 'none' : 'block'
    }} sx={styles.videoStyles(fullscreen, fitVideo)} onLoadedMetadata={handleLoadedMetadata} onEnded={handleEnded} onPlay={() => setIsPlaying(true)} onPause={() => setIsPlaying(false)} onError={handleError} />
    </div>;
};
export default IOSVideoPlayer;